import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { 
  Building, Plus, Users, MapPin, Phone, Mail, Globe, 
  Factory, Briefcase, TrendingUp, Shield, Search, 
  Filter, Grid, List, Sparkles, Building2, BarChart3,
  CheckCircle2, AlertCircle, Clock, RefreshCw
} from 'lucide-react';
import PageHeader from './ui/PageHeader';
import { Progress } from './ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Professional header image - business/corporate
const HEADER_IMAGE_URL = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80";

// Industry configurations with colors
const INDUSTRY_CONFIG = {
  'Construction': { icon: Building2, color: 'from-orange-500 to-amber-500', bg: 'bg-orange-50', text: 'text-orange-600' },
  'Manufacturing': { icon: Factory, color: 'from-blue-500 to-indigo-500', bg: 'bg-blue-50', text: 'text-blue-600' },
  'Healthcare': { icon: Shield, color: 'from-green-500 to-emerald-500', bg: 'bg-green-50', text: 'text-green-600' },
  'Logistics': { icon: Globe, color: 'from-purple-500 to-pink-500', bg: 'bg-purple-50', text: 'text-purple-600' },
  'Energy': { icon: TrendingUp, color: 'from-yellow-500 to-orange-500', bg: 'bg-yellow-50', text: 'text-yellow-600' },
  'default': { icon: Briefcase, color: 'from-gray-500 to-slate-500', bg: 'bg-gray-50', text: 'text-gray-600' }
};

// Feature highlights
const COMPANY_FEATURES = [
  { icon: Building, label: "Company Profiles", description: "Complete business info", color: "text-blue-500", bg: "bg-blue-50" },
  { icon: Users, label: "Team Management", description: "Employee tracking", color: "text-green-500", bg: "bg-green-50" },
  { icon: Shield, label: "Safety Records", description: "Compliance history", color: "text-amber-500", bg: "bg-amber-50" },
  { icon: BarChart3, label: "Analytics", description: "Performance metrics", color: "text-purple-500", bg: "bg-purple-50" }
];

const CompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    address: '',
    phone: '',
    email: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load companies",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const companyData = {
        ...formData,
        contact_info: {
          phone: formData.phone,
          email: formData.email
        }
      };
      delete companyData.phone;
      delete companyData.email;

      await axios.post(`${API}/companies`, companyData);
      toast({
        title: "Success",
        description: "Company created successfully!"
      });
      setShowCreateForm(false);
      setFormData({ name: '', industry: '', address: '', phone: '', email: '' });
      fetchCompanies();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create company",
        variant: "destructive"
      });
    }
  };

  const getIndustryConfig = (industry) => {
    return INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG.default;
  };

  const filteredCompanies = companies.filter(company => 
    company.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.industry?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate stats
  const stats = {
    total: companies.length,
    industries: [...new Set(companies.map(c => c.industry).filter(Boolean))].length,
    withContact: companies.filter(c => c.contact_info?.email || c.contact_info?.phone).length,
    recent: companies.filter(c => {
      const created = new Date(c.created_at);
      const now = new Date();
      return (now - created) < 30 * 24 * 60 * 60 * 1000; // Last 30 days
    }).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unified Page Header */}
      <PageHeader
        title="Company Management"
        subtitle="Manage client companies, track safety compliance, and maintain comprehensive business records for audits"
        icon={Building}
        variant="blue"
        onRefresh={fetchCompanies}
        loading={loading}
        actions={
          <Button 
            onClick={() => setShowCreateForm(true)} 
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white border-0"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Company
          </Button>
        }
        stats={[
          { icon: Building, label: 'Companies', value: stats.total, color: 'blue' },
          { icon: Factory, label: 'Industries', value: stats.industries, color: 'purple' },
          { icon: CheckCircle2, label: 'With Contact', value: stats.withContact, color: 'green' },
          { icon: Clock, label: 'Recent', value: stats.recent, color: 'cyan' }
        ]}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white rounded-2xl p-4 shadow-sm">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input 
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64 rounded-xl"
            />
          </div>
          
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                viewMode === 'grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid className="w-4 h-4" />
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
          </div>
        </div>
        
        {/* Feature Cards */}
        <div className="flex gap-2">
          {COMPANY_FEATURES.map((feature, idx) => (
            <div 
              key={idx}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl ${feature.bg} cursor-pointer hover:shadow-md transition-all`}
            >
              <feature.icon className={`w-4 h-4 ${feature.color}`} />
              <span className="text-sm font-medium text-gray-700">{feature.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl shadow-sm border-t-4 border-t-blue-500 hover:shadow-lg transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Companies</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-1">registered clients</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <Building className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm border-t-4 border-t-purple-500 hover:shadow-lg transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Industries</p>
                <p className="text-3xl font-bold text-gray-900">{stats.industries}</p>
                <p className="text-xs text-gray-400 mt-1">sectors covered</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg">
                <Factory className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm border-t-4 border-t-green-500 hover:shadow-lg transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">With Contact Info</p>
                <p className="text-3xl font-bold text-gray-900">{stats.withContact}</p>
                <p className="text-xs text-gray-400 mt-1">complete profiles</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm border-t-4 border-t-amber-500 hover:shadow-lg transition-all">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Recent (30 days)</p>
                <p className="text-3xl font-bold text-gray-900">{stats.recent}</p>
                <p className="text-xs text-gray-400 mt-1">new additions</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Company Form Modal */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Building className="w-5 h-5 text-white" />
              </div>
              Add New Company
            </DialogTitle>
            <DialogDescription>
              Enter company details for safety audit management
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCompany} className="space-y-4">
            <div>
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Enter company name"
                className="rounded-xl"
                required
              />
            </div>
            <div>
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) => setFormData({...formData, industry: e.target.value})}
                placeholder="e.g., Construction, Manufacturing"
                className="rounded-xl"
                required
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Full business address"
                className="rounded-xl"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(555) 000-0000"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="contact@company.com"
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Company
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Companies List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map((company) => {
            const industryConfig = getIndustryConfig(company.industry);
            const IndustryIcon = industryConfig.icon;
            return (
              <Card 
                key={company.id} 
                className="rounded-2xl shadow-sm hover:shadow-xl transition-all cursor-pointer group overflow-hidden"
              >
                {/* Color Bar */}
                <div className={`h-2 bg-gradient-to-r ${industryConfig.color}`}></div>
                
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${industryConfig.color} flex items-center justify-center shadow-lg`}>
                        <IndustryIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">
                          {company.name}
                        </CardTitle>
                        <Badge className={`${industryConfig.bg} ${industryConfig.text} mt-1`}>
                          {company.industry || 'General'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {company.address && (
                      <div className="flex items-start gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">{company.address}</span>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-3 pt-2 border-t border-gray-100">
                      {company.contact_info?.phone && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {company.contact_info.phone}
                        </div>
                      )}
                      {company.contact_info?.email && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {company.contact_info.email}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List View */
        <Card className="rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Industry</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCompanies.map((company) => {
                  const industryConfig = getIndustryConfig(company.industry);
                  const IndustryIcon = industryConfig.icon;
                  return (
                    <tr key={company.id} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${industryConfig.color} flex items-center justify-center`}>
                            <IndustryIcon className="w-5 h-5 text-white" />
                          </div>
                          <span className="font-medium text-gray-900">{company.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={`${industryConfig.bg} ${industryConfig.text}`}>
                          {company.industry || 'General'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        {company.address || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col text-sm text-gray-600">
                          {company.contact_info?.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {company.contact_info.phone}
                            </span>
                          )}
                          {company.contact_info?.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {company.contact_info.email}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {filteredCompanies.length === 0 && (
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="text-center py-16">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Building className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchQuery ? 'No companies found' : 'No companies yet'}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchQuery 
                ? `No companies match "${searchQuery}". Try a different search term.`
                : 'Add your first company to start managing safety audits and compliance tracking.'
              }
            </p>
            {!searchQuery && (
              <Button 
                onClick={() => setShowCreateForm(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Company
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompaniesPage;
